import React, { ReactNode, useEffect, useState, useRef } from "react";
import classNames from "classnames";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Tooltip,
} from "@hid-galaxy-ui/galaxy-react";
import { SideNavVariants } from "./SideNavEnum";
import { Placement } from "@hid-galaxy-ui/galaxy-react/components/Tooltip/TooltipEnums";
import { pencilIcon, chevronUpIcon} from "../components/Icons";
import { getText } from "../__utils__/textUtils";

interface iSubNavItem {
  title: string;
  key: string;
  disabled?: boolean;
}

interface iNavItem {
  title: string;
  key: string;
  iconName?: any;
  subNavItems?: Array<iSubNavItem>;
  disabled?: boolean;
  hasSubNav?: boolean;
}

export interface SideNavProps {
  isToggle?: boolean;
  mode?: string;
  id: string;
  onNodeClick: (e: React.MouseEvent<HTMLElement>, nodeKey: string) => void;
  navItems: Array<iNavItem>;
  selected?: string;
  noSubNavItemMessage?: string;
  children?: ReactNode;
  onNewChat?: () => void;
  username?: string;
  onUserAction?: (action: string) => void;
  forceExpanded?: boolean;
}

export default function SideNav(props: SideNavProps) {
  const {
    isToggle = true,
    mode = SideNavVariants.Expanded,
    id,
    navItems,
    onNodeClick,
    noSubNavItemMessage = "No Items",
    selected,
    children,
    onNewChat,
    username,
    onUserAction,
    forceExpanded = false,
  } = props;
  const [navState, setNavState] = useState(mode);

  // Use forced expanded state when forceExpanded is true
  const effectiveNavState = forceExpanded ? SideNavVariants.Expanded : navState;

  const [currentSelectedKeys, setCurrentSelectedKeys] = useState<{
    navKey: Array<string>;
    subNavKey: string;
  }>({
    navKey: [],
    subNavKey: "",
  });
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLDivElement>(null);
  const onClick = (e: React.MouseEvent<HTMLElement>, nodeKey: string) => {
    setCurrentSelectedKeys({ ...currentSelectedKeys, subNavKey: nodeKey });
    onNodeClick(e, nodeKey);
  };
  const isCurrentNavKeyExists = (item: string) => {
    return currentSelectedKeys.navKey.find((el) => el === item) ? true : false;
  };
  useEffect(() => {
    setNavState(mode);
  }, [mode]);

  // Handle clicks outside the user dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isUserDropdownOpen) {
        const target = event.target as Node;
        const dropdownElement = userDropdownRef.current;
        const buttonElement = userButtonRef.current;
        
        // Check if click is outside both dropdown and button
        if (
          dropdownElement && 
          buttonElement && 
          !dropdownElement.contains(target) && 
          !buttonElement.contains(target)
        ) {
          setIsUserDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen]);
  useEffect(() => {
    if (
      (selected && isCurrentNavKeyExists(selected)) ||
      selected === currentSelectedKeys.subNavKey
    )
      return;
    navItems.forEach((nav) => {
      if (nav.key === selected) {
        effectiveNavState === SideNavVariants.Expanded &&
          setCurrentSelectedKeys({
            navKey: [...currentSelectedKeys.navKey, nav.key],
            subNavKey: !nav.subNavItems ? nav.key : "",
          });
        return;
      }
      const selectedSubNav = nav.subNavItems?.find(
        (subNav) => subNav.key === selected
      );

      if (selectedSubNav) {
        effectiveNavState === SideNavVariants.Expanded &&
          setCurrentSelectedKeys({
            navKey: [...currentSelectedKeys.navKey, nav.key],
            subNavKey: selectedSubNav.key,
          });
        return;
      }
    });
  }, [navItems, selected]);

  const toggleSideNav = () => {
    const currentState =
      effectiveNavState === SideNavVariants.Expanded
        ? SideNavVariants.Colapsed
        : SideNavVariants.Expanded;
    setNavState(currentState);
    if (currentState === SideNavVariants.Colapsed) {
      setCurrentSelectedKeys({ navKey: [], subNavKey: "" });
    }
  };

  const setToggleToCurrentSelected = (key: string, hasSubNav: boolean) => {
    setCurrentSelectedKeys({
      ...currentSelectedKeys,
      navKey: filterArray(key),
    });
    if (hasSubNav && !forceExpanded) {
      setNavState(SideNavVariants.Expanded);
    }
  };
  const filterArray = (el: string) => {
    const selectedNavKeys = currentSelectedKeys.navKey;
    const selectedIndex = selectedNavKeys?.findIndex((nav) => nav === el);
    if (selectedIndex === -1) {
      selectedNavKeys.push(el);
    } else {
      selectedNavKeys.splice(selectedIndex, 1);
    }
    return selectedNavKeys;
  };

  const renderNavIcon = (iconName: string) => {
    return <Iconography icon={iconName} size={IconographySizes.Medium} />;
  };

  const handleUserProfileClick = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const handleUserAction = (action: string) => {
    setIsUserDropdownOpen(false);
    if (onUserAction) {
      onUserAction(action);
    }
  };

  const renderSubNav = (subNavItems: Array<iSubNavItem> | undefined) => {
    return subNavItems && subNavItems?.length > 0 ? (
      <ul className={`hid-side-nav__sub-menu`}>
        {subNavItems?.map((subNavItem: iSubNavItem) => (
          <li className="hid-side-nav__sub-menu-item" key={subNavItem.key}>
            <button
              disabled={subNavItem.disabled}
              id={subNavItem.key}
              className={classNames(
                "hid-side-nav__sub-menu-link hid-text-truncate",
                currentSelectedKeys.subNavKey === subNavItem.key &&
                  !subNavItem.disabled
                  ? "hid-side-nav__sub-menu-link--active"
                  : ""
              )}
              onClick={(e) => onClick(e, subNavItem.key)}
              title={subNavItem.title}
            >
              {subNavItem?.title}
            </button>
          </li>
        ))}
      </ul>
    ) : (
      <div className="hid-text-center hid-side-nav__no-record">
        {noSubNavItemMessage}
      </div>
    );
  };

  const renderNavItem = (navItem: iNavItem) => {
    const hasSubNav = !!(
      (navItem.subNavItems && navItem.subNavItems?.length > 0) ||
      navItem?.hasSubNav
    );
    return (
      <li
        key={navItem.key}
        className={navItem.iconName ? "hid-side-nav--has-icon" : ""}
      >
        <button
          id={navItem.key}
          disabled={navItem.disabled}
          onClick={(e) =>
            hasSubNav
              ? setToggleToCurrentSelected(navItem.key, hasSubNav)
              : onClick(e, navItem.key)
          }
          className={`hid-side-nav__menu-item hid-full-width ${
            currentSelectedKeys.subNavKey === navItem.key && !navItem.disabled
              ? "hid-side-nav__menu-item--active"
              : ""
          }`}
          aria-expanded={currentSelectedKeys.navKey.includes(navItem.key)}
        >
          {navItem.iconName ? (
            <div className="hid-side-nav__icon">
              {effectiveNavState === SideNavVariants.Expanded ? (
                <span className="hid-layout__mr-03">
                  {renderNavIcon(navItem.iconName)}
                </span>
              ) : (
                <Tooltip interactive={true} message={navItem.title}>
                  <span className="hid-layout__pr-02">
                    {renderNavIcon(navItem.iconName)}
                  </span>
                </Tooltip>
              )}
            </div>
          ) : null}

          {effectiveNavState === SideNavVariants.Expanded && (
            <>
              <div
                className="hid-side-nav__link hid-text-truncate"
                title={navItem.title}
              >
                {navItem.title}
              </div>
              {hasSubNav && (
                <div className="hid-side-nav__icon">
                  <Iconography
                    icon={
                      currentSelectedKeys.navKey.includes(navItem.key)
                        ? "chevronUp"
                        : "chevronDown"
                    }
                  />
                </div>
              )}
            </>
          )}
        </button>
        {currentSelectedKeys.navKey.includes(navItem.key) && (
          <>
            {navItem.subNavItems && renderSubNav(navItem.subNavItems)}
            {!navItem.subNavItems && navItem?.hasSubNav && (
              <div className="hid-text-center hid-side-nav__loader">
                <Loader variant={LoaderSizes.medium} />
              </div>
            )}
          </>
        )}
      </li>
    );
  };

  return (
    <>
      <aside
        className={`hid-side-nav hid-side-nav--${effectiveNavState.toLowerCase()}`}
        data-testid="sideNav"
        id={id}
        aria-label="Side navigation"
      >
        {isToggle ? (
          <div className="hid-side-nav__collapse-icon">
            <Tooltip 
              interactive={true} 
              message={effectiveNavState === SideNavVariants.Expanded ? "Hide sidebar" : "Expand sidebar"} 
              placement={Placement.Bottom}
            >
            <button
              className="hid-side-nav__icon-button"
              onClick={() => toggleSideNav()}
              aria-expanded={effectiveNavState === SideNavVariants.Expanded}
            >
              <Iconography
                icon={
                  effectiveNavState === SideNavVariants.Expanded
                    ? "chevronsLeft"
                    : "chevronsRight"
                }
              />
            </button>
            </Tooltip>
          </div>
        ) : null}

        {/* Pencil Button - Collapsed Only */}
        {effectiveNavState === SideNavVariants.Colapsed && onNewChat && (
          <div className="hid-side-nav__pencil-button">
            <Tooltip interactive={true} message="New Chat" placement={Placement.Bottom}>
            <Button
              icon={pencilIcon()}
              onClick={onNewChat}
              variant="primary"
            />
            </Tooltip>
          </div>
        )}

        {/* Top Section - Fixed */}
        <div className="hid-side-nav__top-section">
          {/* Mobile Assistant Title */}
          {effectiveNavState === SideNavVariants.Expanded && (
            <div className="hid-side-nav__assistant-title">
              <h2 className="hid-side-nav__assistant-title-text">
              AIDEN - HR Assistant
              </h2>
            </div>
          )}

          {/* NEW CHAT Button - Expanded */}
          {effectiveNavState === SideNavVariants.Expanded && onNewChat && (
            <div className="hid-side-nav__new-chat-section">
              <Button
                icon={pencilIcon()}
                label="NEW CHAT"
                onClick={onNewChat}
                variant="primary"
              >
               getText("NEW_CHAT")
              </Button>
            </div>
          )}

          {/* Conversation History Section */}
          {effectiveNavState === SideNavVariants.Expanded &&
            navItems.length > 0 && (
              <div className="hid-side-nav__conversation-history">
                <h3 className="hid-side-nav__conversation-history-title">
                  Conversation History
                </h3>
              </div>
            )}
        </div>

        {/* Middle Section - Scrollable */}
        <div className="hid-side-nav__middle-section">
          <ul className="hid-side-nav__menu">
            {navItems.map((navItem) => renderNavItem(navItem))}
          </ul>
          {effectiveNavState === SideNavVariants.Expanded && children && (
            <div className="hid-side-nav__content">{children}</div>
          )}
        </div>

        {/* Bottom Section - Fixed */}
        <div className="hid-side-nav__bottom-section">
          {/* User Profile Section - Expanded */}
          {effectiveNavState === SideNavVariants.Expanded && username && (
            <div className="hid-side-nav__user-profile-container" ref={userButtonRef}>
              <div
                className="hid-side-nav__user-profile"
                onClick={handleUserProfileClick}
              >
                <div className="hid-side-nav__user-info">
                  <Iconography
                    icon="circleUser"
                    size={IconographySizes.Medium}
                  />
                  <span className="hid-side-nav__username">{username}</span>
                </div>
                {chevronUpIcon()}
              </div>

              {/* User Dropdown Menu */}
              {isUserDropdownOpen && (
                <div className="hid-side-nav__user-dropdown" ref={userDropdownRef}>
                  <div
                    className="hid-side-nav__dropdown-item"
                    onClick={() => handleUserAction("logout")}
                  >
                    <Iconography
                      icon="arrowRightFromBracket"
                      size={IconographySizes.Medium}
                    />
                    <span>Logout</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* User Icon - Collapsed Only (Outside Sidenav) */}
      {effectiveNavState === SideNavVariants.Colapsed && username && (
        <div className="hid-side-nav__user-icon-floating">
          <div className="hid-side-nav__user-profile-container" ref={userButtonRef}>
            <Button
              variant="secondary"
              onClick={handleUserProfileClick}
              icon={
                <div className="hid-side-nav__user-icon-container">
                  <Iconography
                    icon="circleUser"
                    size={IconographySizes.Medium}
                  />
                  {isUserDropdownOpen && chevronUpIcon()}
                </div>
              }
            />

            {/* User Dropdown Menu - Collapsed */}
            {isUserDropdownOpen && (
              <div className="hid-side-nav__user-dropdown hid-side-nav__user-dropdown--floating" ref={userDropdownRef}>
                <div
                  className="hid-side-nav__dropdown-item"
                  onClick={() => handleUserAction("logout")}
                >
                  <Iconography
                    icon="arrowRightFromBracket"
                    size={IconographySizes.Medium}
                  />
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
